import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Images, Maximize2, Play, ShoppingBag, Zap } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { formatIDR, products } from "@/data/products";
import { useTilt } from "@/hooks/useTilt";

function ProductCard({ product, index }) {
  const { t, addToCart, buyNow, setSelectedProduct } = useStore();
  const tilt = useTilt();
  const open = () => setSelectedProduct(product);
  const hasVideo = product.media.some((m) => m.type === "video");
  const sold = product.stock === "sold";

  return (
    <motion.article
      layout
      ref={tilt.ref}
      style={tilt.style}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ delay: index * 0.05, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="card-shine group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-ink-card transition-[border-color,box-shadow] duration-500 hover:border-ember/40 hover:shadow-[0_30px_60px_-30px_rgba(255,107,0,0.35)]"
    >
      <button
        data-testid={`product-open-${product.id}`}
        data-cursor-label={t.catalog.viewDetail}
        onClick={open}
        className="relative block w-full overflow-hidden text-left"
        aria-label={`${t.catalog.viewDetail}: ${product.name}`}
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className={`aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${sold ? "grayscale-[0.6]" : ""}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <span
          data-testid={`product-stock-${product.id}`}
          className={`absolute left-4 top-4 flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${
            sold ? "bg-zinc-900/90 text-zinc-300 ring-1 ring-white/15" : "bg-ember text-black"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${sold ? "bg-zinc-500" : "animate-pulse bg-black/70"}`} />
          {sold ? t.catalog.sold : t.catalog.available}
        </span>
        <span className="absolute right-4 top-4 rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white backdrop-blur-md">
          {product.strain}
        </span>
        <span
          data-testid={`product-media-count-${product.id}`}
          className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full border border-white/15 bg-black/50 px-3 py-1 font-mono text-[10px] tracking-widest text-white backdrop-blur-md"
        >
          <Images size={11} className="text-ember" />
          {product.media.length} {t.catalog.photos}
          {hasVideo && <Play size={10} fill="currentColor" className="text-ember" />}
        </span>
        <span className="absolute bottom-4 right-4 flex translate-y-3 items-center gap-2 rounded-full bg-white px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-black opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <Maximize2 size={11} />
          {t.catalog.viewDetail}
        </span>
      </button>

      <div className="flex flex-1 flex-col p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">
          {product.category}
        </p>
        <h3
          data-testid={`product-name-${product.id}`}
          onClick={open}
          className="mt-2 cursor-pointer font-display text-lg font-bold leading-snug text-white transition-colors hover:text-ember-light"
        >
          {product.name}
        </h3>
        <p
          data-testid={`product-price-${product.id}`}
          className="mt-3 font-mono text-base font-bold text-ember-light"
        >
          {formatIDR(product.price)}
        </p>

        <div className="mt-6 grid grid-cols-1 gap-2.5 xl:grid-cols-2">
          <button
            data-testid={`add-to-cart-${product.id}`}
            disabled={sold}
            onClick={() => {
              addToCart(product);
              toast.success(t.toast.added);
            }}
            className="flex items-center justify-center gap-2 rounded-full border border-white/20 px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:border-ember hover:text-ember disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/20 disabled:hover:text-white"
          >
            <ShoppingBag size={13} />
            {t.catalog.addToCart}
          </button>
          <button
            data-testid={`buy-now-${product.id}`}
            disabled={sold}
            onClick={() => buyNow(product)}
            className="flex items-center justify-center gap-2 rounded-full bg-ember px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-widest text-black transition-all duration-300 hover:bg-ember-light hover:shadow-[0_0_24px_rgba(255,107,0,0.35)] disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500 disabled:hover:shadow-none"
          >
            <Zap size={13} />
            {sold ? t.catalog.sold : t.catalog.buyNow}
          </button>
        </div>
      </div>
    </motion.article>
  );
}

export default function Catalog() {
  const { t } = useStore();
  const [filter, setFilter] = useState("all");

  const visible = products.filter((p) => filter === "all" || p.category === filter);

  return (
    <section id="koleksi" data-testid="catalog-section" className="relative bg-ink-soft py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-ember" />
              <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-ember">
                {t.catalog.eyebrow}
              </span>
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {t.catalog.title}
            </h2>
            <p className="mt-4 max-w-md text-base text-mist">{t.catalog.subtitle}</p>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {t.catalog.filters.map((f) => (
              <button
                key={f.id}
                data-testid={`category-filter-${f.id.toLowerCase()}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full border px-5 py-2 font-mono text-[11px] font-bold uppercase tracking-widest transition-all duration-300 ${
                  filter === f.id
                    ? "border-ember bg-ember text-black"
                    : "border-white/15 text-mist hover:border-ember/50 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
